import { Logger } from '../../../common/utils';
import { ATLASSIAN_CURRENT_LOCALE_COOKIE_KEY } from '../../../constants';
import { fetchCurrentLocale, saveConsentPreferences } from '../../../services/consent-hub-service';
import { checkIfTokenIsDefault, StatusCodeError } from '../../../services/consent-hub-service/utils';
import { ConsentPreference } from '../../../types';
import { getCookie } from '../get-cookie';
import { overrideLocaleForLiveTesting } from '../override-with-testing-cookies';
import { setConsentTokenCookie, setConsentTokenFallbackCookie, setShouldShowBannerCookie } from '../set-required-consent-cookies';
import { packUserPreferences } from '../transformer';
import { mergeConsentUpdates, onlySetFallbackIfDifferent } from './utils';
export const createConsentTokenCookies = (mergedPrefs, currentLocale, usingPrefetchedData) => {
  // On a successful save, transform the prefs object into a formatted consentToken bit string
  // Then store the new token in a 10min-expiry consent cookie and a 1yr-expiry fallback cookie

  const consentToken = packUserPreferences(mergedPrefs);
  // If the token is prefetched and a default, we should rely on the existing cookie data rather than re-setting
  // cookies that are already set
  const tokenIsPrefetchedDefault = usingPrefetchedData && checkIfTokenIsDefault(consentToken);
  if (!tokenIsPrefetchedDefault) {
    if (usingPrefetchedData) {
      // If the fallback token already exists, we don't want to nuke the expiration inadvertently (since this happens on every page load).
      onlySetFallbackIfDifferent(consentToken);
    } else {
      // This has originated from a user saving their consents in the UI, so set a fallback to their new consents immediately.
      setConsentTokenFallbackCookie(consentToken);
    }
    setConsentTokenCookie(consentToken);
    setShouldShowBannerCookie(consentToken, currentLocale);
  }
};
export const createConsentHubUnavailableBasedTokens = (err, mergedPrefs, currentLocale) => {
  const consentToken = packUserPreferences({
    ...mergedPrefs,
    [ConsentPreference.ConsentDataUnavailable]: true // Indicates we were unable to hit ConsentHub when the consents were created
  });
  if (err instanceof StatusCodeError && err.status === 401) {
    // When the failure is derived from a local-only/unauthenticated user, we need to also set the consent token
    // Otherwise, we will never actually adhere to their consents, as they'll continue to receive default every time we attempt to unsuccessfully fetch their prefs

    Logger.error(`User is unauthenticated. ${err.message}`);
    onlySetFallbackIfDifferent(consentToken);
    setConsentTokenCookie(consentToken);
    setShouldShowBannerCookie(consentToken, currentLocale);
    return;
  }

  // When there's an actual failure, we only store data in the fallbackConsentToken cookie
  // This allows us to still track consents when ConsentHub is down > 10 minute expiry
  // But do _not_ set a consentToken cookie so we attempt to re-request from ConsentHub on sequential loads
  // This circumvents an issue of a user thinking they saved consents to CH, but when we request them later,
  // CH has no record of them, and they get shown the banner again
  Logger.error('Token failed to save');
  onlySetFallbackIfDifferent(consentToken);
  setShouldShowBannerCookie(consentToken, currentLocale);
};

// This logic only runs when a user initiates saving their consents **OR** when we've been provided prefetched data
// and we need to store the passed values, otherwise we'd have no way to set the associated cookies for the user
export const setConsentToken = async (updatedPreferences, displayedTextMap, usingPrefetchedData) => {
  // currentLocale is used to determine the `consentedUnderXX` bits
  let currentLocale = getCookie(ATLASSIAN_CURRENT_LOCALE_COOKIE_KEY);
  if (!currentLocale) {
    // If the cookie is expired or has never been set, do our best effort to find the current locale
    // If we genuinely cannot find a locale, it'll be set to UNKNOWN
    currentLocale = await fetchCurrentLocale();
  }
  const testingLocale = overrideLocaleForLiveTesting(currentLocale);
  const mergedPrefs = mergeConsentUpdates(updatedPreferences, testingLocale || currentLocale);
  try {
    // usingPrefetchedData— we ended up here from initialPreferences with prefetched data, don't save to ConsentHub
    if (!usingPrefetchedData) {
      // Attempts to save the newly updated preferences object
      // Can only be saved when a user is logged in, so this error will be caught if unauthed
      await saveConsentPreferences(mergedPrefs, displayedTextMap);
    }
    createConsentTokenCookies(mergedPrefs, currentLocale, usingPrefetchedData);
  } catch (err) {
    // If we fail to save to ConsentHub, it could be due to ConsentHub being down, or the user being unauthenticated/local-only
    createConsentHubUnavailableBasedTokens(err, mergedPrefs, currentLocale);
  }
};